import type { EntradasDoAno, EntradasDoMes } from "@base/contracts";

/**
 * Calculo do Funil Projetado, espelho da aba "Funil Projetado" da planilha da
 * Monte, com MQL na cascata:
 *
 *   orcamento x (1 + escala) / CPL = leads
 *   leads x %MQL = MQL -> x %agendamento = agendamentos -> x %consulta =
 *   consultas -> x %venda = vendas -> x ticket = faturamento de marketing
 *
 * Mes sem entrada nao zera a meta: o orcamento herda o mes anterior; os leads
 * herdam o anterior (2o mes) ou a mediana dos ate 3 anteriores; as etapas
 * arredondadas e os custos herdam o mes anterior. E o comportamento da planilha,
 * para quem preenche so alguns meses.
 *
 * Dinheiro em centavos (pode ter fracao no meio da conta; arredonde so ao exibir).
 */

export type MetaDoMes = {
  readonly orcamentoCentavos: number;
  readonly cplCentavos: number | null;
  readonly leads: number;
  readonly mql: number;
  readonly agendamentos: number;
  readonly consultas: number;
  readonly vendas: number;
  readonly taxaMql: number | null;
  readonly taxaAgendamento: number | null;
  readonly taxaConsulta: number | null;
  readonly taxaVenda: number | null;
  readonly ticketMedioCentavos: number | null;
  readonly faturamentoMarketingCentavos: number;
  readonly faturamentoClinicaCentavos: number;
  readonly faturamentoTotalCentavos: number;
  readonly roas: number | null;
  readonly custoPorConsultaCentavos: number | null;
  readonly cpaCentavos: number | null;
};

export type FunilCalculado = {
  readonly meses: readonly MetaDoMes[];
  readonly total: MetaDoMes;
};

const mediana = (valores: readonly number[]): number => {
  if (valores.length === 0) return 0;
  const ordenados = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  return ordenados.length % 2 === 0
    ? ((ordenados[meio - 1] ?? 0) + (ordenados[meio] ?? 0)) / 2
    : (ordenados[meio] ?? 0);
};

const naoZero = (valor: number): number | null => (valor > 0 ? valor : null);
const dividir = (a: number, b: number): number | null => (b > 0 ? a / b : null);

/** Media dos meses preenchidos (como o AVERAGEIF "<>0" da planilha). */
const mediaPreenchida = (valores: readonly number[]): number | null => {
  const preenchidos = valores.filter((valor) => valor > 0);
  return preenchidos.length === 0 ? null : preenchidos.reduce((a, b) => a + b, 0) / preenchidos.length;
};

export function calcularFunil(entradas: EntradasDoAno): FunilCalculado {
  const meses: MetaDoMes[] = [];

  entradas.meses.forEach((mes: EntradasDoMes, indice) => {
    const anterior = meses[indice - 1];
    const orcamento =
      mes.orcamentoCentavos > 0 ? mes.orcamentoCentavos * (1 + entradas.escalaDeVerba) : (anterior?.orcamentoCentavos ?? 0);

    const leadsDoOrcamento = mes.cplCentavos > 0 ? orcamento / mes.cplCentavos : 0;
    const leads =
      leadsDoOrcamento > 0
        ? leadsDoOrcamento
        : indice === 0
          ? 0
          : indice === 1
            ? (anterior?.leads ?? 0)
            : mediana(meses.slice(Math.max(0, indice - 3)).map((item) => item.leads));

    // Etapa arredondada; zerada herda o mes anterior.
    const etapa = (base: number, taxa: number, herdado: number | undefined) => {
      const valor = Math.round(base * taxa);
      return valor === 0 && herdado !== undefined ? herdado : valor;
    };
    const mql = etapa(leads, mes.taxaMql, anterior?.mql);
    const agendamentos = etapa(mql, mes.taxaAgendamento, anterior?.agendamentos);
    const consultas = etapa(agendamentos, mes.taxaConsulta, anterior?.consultas);
    const vendas = etapa(consultas, mes.taxaVenda, anterior?.vendas);

    const faturamentoMarketing = vendas * mes.ticketMedioCentavos;
    const faturamentoClinica = mes.faturamentoClinicaCentavos;

    meses.push({
      orcamentoCentavos: orcamento,
      cplCentavos: naoZero(mes.cplCentavos),
      leads,
      mql,
      agendamentos,
      consultas,
      vendas,
      taxaMql: naoZero(mes.taxaMql),
      taxaAgendamento: naoZero(mes.taxaAgendamento),
      taxaConsulta: naoZero(mes.taxaConsulta),
      taxaVenda: naoZero(mes.taxaVenda),
      ticketMedioCentavos: naoZero(mes.ticketMedioCentavos),
      faturamentoMarketingCentavos: faturamentoMarketing,
      faturamentoClinicaCentavos: faturamentoClinica,
      faturamentoTotalCentavos: faturamentoMarketing + faturamentoClinica,
      roas: dividir(faturamentoMarketing, orcamento),
      custoPorConsultaCentavos: dividir(orcamento, consultas) ?? anterior?.custoPorConsultaCentavos ?? null,
      cpaCentavos: dividir(orcamento, vendas) ?? anterior?.cpaCentavos ?? null,
    });
  });

  const somar = (campo: (mes: MetaDoMes) => number) => meses.reduce((total, mes) => total + campo(mes), 0);
  const orcamento = somar((mes) => mes.orcamentoCentavos);
  const consultas = somar((mes) => mes.consultas);
  const vendas = somar((mes) => mes.vendas);
  const faturamentoMarketing = somar((mes) => mes.faturamentoMarketingCentavos);
  const faturamentoClinica = somar((mes) => mes.faturamentoClinicaCentavos);

  return {
    meses,
    total: {
      orcamentoCentavos: orcamento,
      cplCentavos: mediaPreenchida(entradas.meses.map((mes) => mes.cplCentavos)),
      leads: somar((mes) => mes.leads),
      mql: somar((mes) => mes.mql),
      agendamentos: somar((mes) => mes.agendamentos),
      consultas,
      vendas,
      taxaMql: mediaPreenchida(entradas.meses.map((mes) => mes.taxaMql)),
      taxaAgendamento: mediaPreenchida(entradas.meses.map((mes) => mes.taxaAgendamento)),
      taxaConsulta: mediaPreenchida(entradas.meses.map((mes) => mes.taxaConsulta)),
      taxaVenda: mediaPreenchida(entradas.meses.map((mes) => mes.taxaVenda)),
      ticketMedioCentavos: mediaPreenchida(entradas.meses.map((mes) => mes.ticketMedioCentavos)),
      faturamentoMarketingCentavos: faturamentoMarketing,
      faturamentoClinicaCentavos: faturamentoClinica,
      faturamentoTotalCentavos: faturamentoMarketing + faturamentoClinica,
      roas: dividir(faturamentoMarketing, orcamento),
      custoPorConsultaCentavos: dividir(orcamento, consultas),
      cpaCentavos: dividir(orcamento, vendas),
    },
  };
}
